/**
 * SectionEditor — Modal editor for section content.
 * Extracted from PageBuilder.js for modularity.
 */

import React, { useState, useEffect } from 'react';
import { Save, X } from 'lucide-react';
import { buttonStyles } from '../../utils/colors';
import { hasSection } from '../componentDefinitions';
import { getDynamicSection } from '../../utils/sectionRegistry';
import SectionPropertiesPanel from './SectionPropertiesPanel';
import { getDefaultStyleValue, SECTION_TYPES } from './utils/builderHelpers';

export default function SectionEditor({ section, sectionType, onSave, onCancel, allSectionTypes, page }) {
  const [content, setContent] = useState(
    section ? { ...section.content } : { 
      ...((allSectionTypes && allSectionTypes[sectionType]?.defaultContent) || SECTION_TYPES[sectionType]?.defaultContent || {})
    }
  );

  // Initialize dynamic sections with proper column structure
  useEffect(() => {
    if (allSectionTypes && allSectionTypes[sectionType]?.isDynamic) {
      const dynamicSection = getDynamicSection(sectionType);
      if (dynamicSection && dynamicSection.settings && dynamicSection.settings.columnStructure) {
        const testimonialCount = content.testimonialCount || 3;
        const currentTestimonials = content.actualColumns || [];
        
        // If we need more testimonials than we have, add them
        if (currentTestimonials.length < testimonialCount) {
          const defaultTestimonials = [...currentTestimonials];
          
          for (let i = currentTestimonials.length; i < testimonialCount; i++) {
            const testimonial = {
              id: `testimonial-${Date.now()}-${i}`
            };
            
            // Add default values for each field in the column structure
            dynamicSection.settings.columnStructure.fields.forEach(field => {
              testimonial[field.key] = field.default || '';
              
              // Add style defaults based on possible_styles
              if (field.possible_styles) {
                field.possible_styles.forEach(style => {
                  testimonial[`${field.key}${style.charAt(0).toUpperCase() + style.slice(1)}`] = getDefaultStyleValue(style);
                });
              }
            });
            
            defaultTestimonials.push(testimonial);
          }
          
          setContent(prev => ({ ...prev, actualColumns: defaultTestimonials }));
        }
        // If we have more testimonials than needed, trim them
        else if (currentTestimonials.length > testimonialCount) {
          const trimmedTestimonials = currentTestimonials.slice(0, testimonialCount);
          setContent(prev => ({ ...prev, actualColumns: trimmedTestimonials }));
        }
      }
    }
  }, [section, sectionType, allSectionTypes, content.testimonialCount, content.actualColumns]);

  const updateContent = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onSave({
      ...section,
      section_type: sectionType,
      content
    });
  };

  // Common styling fields component
  const renderSpacingControls = () => (
    <div className="space-y-4 border-t border-gray-200 pt-4">
      <h4 className="text-sm font-medium text-gray-900">Spacing</h4>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Margin Top (px)</label>
          <input
            type="number"
            value={content.marginTop || '0'}
            onChange={(e) => updateContent('marginTop', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            min="0"
            max="500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Margin Bottom (px)</label>
          <input
            type="number"
            value={content.marginBottom || ''}
            onChange={(e) => updateContent('marginBottom', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            min="0"
            max="500"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Padding Top (px)</label>
          <input
            type="number"
            value={content.paddingTop || '0'}
            onChange={(e) => updateContent('paddingTop', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            min="0"
            max="100"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Padding Bottom (px)</label>
          <input
            type="number"
            value={content.paddingBottom || '0'}
            onChange={(e) => updateContent('paddingBottom', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            min="0"
            max="100"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Padding Left (px)</label>
          <input
            type="number"
            value={content.paddingLeft || '0'}
            onChange={(e) => updateContent('paddingLeft', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            min="0"
            max="100"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Padding Right (px)</label>
          <input
            type="number"
            value={content.paddingRight || '0'}
            onChange={(e) => updateContent('paddingRight', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            min="0"
            max="100"
          />
        </div>
      </div>
    </div>
  );

  // Common styling controls
  const renderStyleControls = () => (
    <div className="space-y-4 border-t border-gray-200 pt-4">
      <h4 className="text-sm font-medium text-gray-900">Style & Alignment</h4>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Text Align</label>
          <select
            value={content.textAlign || 'center'}
            onChange={(e) => updateContent('textAlign', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
          <div className="flex items-center space-x-2">
            <input
              type="color"
              value={content.sectionBackgroundColor === 'transparent' ? '#ffffff' : (content.sectionBackgroundColor || '#ffffff')}
              onChange={(e) => updateContent('sectionBackgroundColor', e.target.value)}
              className="w-12 h-10 border border-gray-300 rounded-md cursor-pointer"
            />
            <button
              onClick={() => updateContent('sectionBackgroundColor', 'transparent')}
              className="px-3 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">Section background color</p>
        </div>
      </div>
    </div>
  );

  const renderEditor = () => {
    // Check if this is a built-in section with a definition
    if (hasSection(sectionType)) {
      return (
        <SectionPropertiesPanel
          sectionType={sectionType}
          content={content}
          onUpdate={updateContent}
          showHeader={false}
          page={page}
        />
      );
    }

    // Check if this is a dynamic section
    const dynamicSection = getDynamicSection(sectionType);
    if (dynamicSection && dynamicSection.settings) {
      return renderDynamicSectionEditor(dynamicSection.settings);
    }

    return <div>Unknown section type</div>;
  };

  // Render editor for dynamic sections
  const renderDynamicSectionEditor = (settings) => {
    return (
      <div className="space-y-6">
        {/* Render fields if any */}
        {settings.fields && settings.fields.map(field => (
          <div key={field.key}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
            </label>
            {field.type === 'select' ? (
              <select
                value={content[field.key] || field.default}
                onChange={(e) => updateContent(field.key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {field.options?.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : field.type === 'number' ? (
              <input
                type="number"
                value={content[field.key] || field.default}
                onChange={(e) => updateContent(field.key, e.target.value)}
                min={field.min}
                max={field.max}
                step={field.step}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            ) : (
              <input
                type="text"
                value={content[field.key] || field.default}
                onChange={(e) => updateContent(field.key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            )}
          </div>
        ))}

        {/* Render column structure if any */}
        {settings.columnStructure && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-4">Column Content</h4>
            <div className="space-y-4">
              {content.actualColumns?.map((column, index) => (
                <div key={column.id || index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-sm font-medium text-gray-700">
                      Column {index + 1}
                    </h5>
                    {content.actualColumns.length > 1 && (
                      <button
                        onClick={() => {
                          const newColumns = content.actualColumns.filter((_, i) => i !== index);
                          updateContent('actualColumns', newColumns);
                        }}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {/* Column fields based on structure */}
                  <div className="space-y-3">
                    {settings.columnStructure.fields.map(field => (
                      <div key={field.key}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {field.label}
                        </label>
                        {field.type === 'textarea' ? (
                          <textarea
                            value={column[field.key] || ''}
                            onChange={(e) => {
                              const newColumns = [...content.actualColumns];
                              newColumns[index] = { ...column, [field.key]: e.target.value };
                              updateContent('actualColumns', newColumns);
                            }}
                            placeholder={field.placeholder || field.default}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        ) : field.type === 'color' ? (
                          <input
                            type="color"
                            value={column[field.key] || field.default || '#ffffff'}
                            onChange={(e) => {
                              const newColumns = [...content.actualColumns];
                              newColumns[index] = { ...column, [field.key]: e.target.value };
                              updateContent('actualColumns', newColumns);
                            }}
                            className="w-full h-10 border border-gray-300 rounded-md cursor-pointer"
                          />
                        ) : field.type === 'icon' ? (
                          <input
                            type="text"
                            value={column[field.key] || ''}
                            onChange={(e) => {
                              const newColumns = [...content.actualColumns];
                              newColumns[index] = { ...column, [field.key]: e.target.value };
                              updateContent('actualColumns', newColumns);
                            }}
                            placeholder={field.placeholder || 'Icon name (e.g., Heart, Star)'}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        ) : (
                          <input
                            type="text"
                            value={column[field.key] || ''}
                            onChange={(e) => {
                              const newColumns = [...content.actualColumns];
                              newColumns[index] = { ...column, [field.key]: e.target.value };
                              updateContent('actualColumns', newColumns);
                            }}
                            placeholder={field.placeholder || field.default}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        )}
                        
                        {/* Field style controls */}
                        {field.possible_styles && field.possible_styles.length > 0 && (
                          <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-100">
                            {field.possible_styles.includes('fontSize') && (
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Font Size</label>
                                <input
                                  type="number"
                                  value={column[`${field.key}FontSize`] || '16'}
                                  onChange={(e) => {
                                    const newColumns = [...content.actualColumns];
                                    newColumns[index] = { ...column, [`${field.key}FontSize`]: e.target.value };
                                    updateContent('actualColumns', newColumns);
                                  }}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                  min="8"
                                  max="72"
                                />
                              </div>
                            )}
                            {field.possible_styles.includes('fontWeight') && (
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Weight</label>
                                <select
                                  value={column[`${field.key}FontWeight`] || 'normal'}
                                  onChange={(e) => {
                                    const newColumns = [...content.actualColumns];
                                    newColumns[index] = { ...column, [`${field.key}FontWeight`]: e.target.value };
                                    updateContent('actualColumns', newColumns);
                                  }}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                >
                                  <option value="normal">Normal</option>
                                  <option value="medium">Medium</option>
                                  <option value="semibold">Semibold</option>
                                  <option value="bold">Bold</option>
                                </select>
                              </div>
                            )}
                            {field.possible_styles.includes('color') && (
                              <div className="col-span-2">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
                                <input
                                  type="color"
                                  value={column[`${field.key}Color`] || '#1f2937'}
                                  onChange={(e) => {
                                    const newColumns = [...content.actualColumns];
                                    newColumns[index] = { ...column, [`${field.key}Color`]: e.target.value };
                                    updateContent('actualColumns', newColumns);
                                  }}
                                  className="w-full h-8 border border-gray-300 rounded cursor-pointer"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )) || null}
              
              {/* Add column button */}
              {content.actualColumns && content.actualColumns.length < (settings.columnStructure.maxColumns || 3) && (
                <button
                  onClick={() => {
                    const newColumn = {
                      id: `column-${Date.now()}`
                    };
                    
                    // Add default values for each field in the column structure
                    settings.columnStructure.fields.forEach(field => {
                      newColumn[field.key] = field.default || '';
                      
                      // Add style defaults based on possible_styles
                      if (field.possible_styles) {
                        field.possible_styles.forEach(style => {
                          newColumn[`${field.key}${style.charAt(0).toUpperCase() + style.slice(1)}`] = getDefaultStyleValue(style);
                        });
                      }
                    });
                    
                    updateContent('actualColumns', [...content.actualColumns, newColumn]);
                  }}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600"
                >
                  + Add Column
                </button>
              )}
            </div>
          </div>
        )}

        {/* Render legacy columns if any */}
        {settings.columns && !settings.columnStructure && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-4">Content Columns</h4>
            <div className="space-y-4">
              {content.columns?.map((column, index) => (
                <div key={column.id || index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-sm font-medium text-gray-700">
                      {settings.columns[index]?.label || `Column ${index + 1}`}
                    </h5>
                    {content.columns.length > 1 && (
                      <button
                        onClick={() => {
                          const newColumns = content.columns.filter((_, i) => i !== index);
                          updateContent('columns', newColumns);
                        }}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {/* Column content based on type */}
                  {settings.columns[index]?.type === 'header' && (
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={column.title || column.text || ''}
                        onChange={(e) => {
                          const newColumns = [...content.columns];
                          newColumns[index] = { ...column, title: e.target.value, text: e.target.value };
                          updateContent('columns', newColumns);
                        }}
                        placeholder={settings.columns[index].default}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      {renderColumnStyleControls(column, index, settings.columns[index])}
                    </div>
                  )}
                  
                  {settings.columns[index]?.type === 'text' && (
                    <div className="space-y-3">
                      <textarea
                        value={column.text || ''}
                        onChange={(e) => {
                          const newColumns = [...content.columns];
                          newColumns[index] = { ...column, text: e.target.value };
                          updateContent('columns', newColumns);
                        }}
                        placeholder={settings.columns[index].default}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      {renderColumnStyleControls(column, index, settings.columns[index])}
                    </div>
                  )}

                  {settings.columns[index]?.type === 'image' && (
                    <div className="space-y-3">
                      <input
                        type="url"
                        value={column.src || ''}
                        onChange={(e) => {
                          const newColumns = [...content.columns];
                          newColumns[index] = { ...column, src: e.target.value };
                          updateContent('columns', newColumns);
                        }}
                        placeholder="Image URL"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <input
                        type="text"
                        value={column.alt || ''}
                        onChange={(e) => {
                          const newColumns = [...content.columns];
                          newColumns[index] = { ...column, alt: e.target.value };
                          updateContent('columns', newColumns);
                        }}
                        placeholder="Alt text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  )}
                </div>
              )) || null}
              
              {/* Add column button */}
              {content.columns && content.columns.length < settings.columns.length && (
                <button
                  onClick={() => {
                    const nextColumnIndex = content.columns.length;
                    const columnSettings = settings.columns[nextColumnIndex];
                    const newColumn = {
                      id: `column-${Date.now()}`,
                      type: columnSettings.type,
                      [columnSettings.type === 'header' ? 'title' : columnSettings.type === 'text' ? 'text' : 'content']: columnSettings.default || '',
                      ...columnSettings.possible_styles?.reduce((acc, style) => {
                        acc[style] = getDefaultStyleValue(style);
                        return acc;
                      }, {}) || {}
                    };
                    updateContent('columns', [...content.columns, newColumn]);
                  }}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600"
                >
                  + Add {settings.columns[content.columns?.length]?.label || 'Column'}
                </button>
              )}
            </div>
          </div>
        )}

        {renderStyleControls()}
        {renderSpacingControls()}
      </div>
    );
  };

  // Helper function for column style controls
  const renderColumnStyleControls = (column, columnIndex, columnSettings) => {
    if (!columnSettings.possible_styles) return null;

    return (
      <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100">
        {columnSettings.possible_styles.includes('fontSize') && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Font Size</label>
            <input
              type="number"
              value={column.fontSize || '16'}
              onChange={(e) => {
                const newColumns = [...content.columns];
                newColumns[columnIndex] = { ...column, fontSize: e.target.value };
                updateContent('columns', newColumns);
              }}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              min="8"
              max="72"
            />
          </div>
        )}
        {columnSettings.possible_styles.includes('fontWeight') && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Weight</label>
            <select
              value={column.fontWeight || 'normal'}
              onChange={(e) => {
                const newColumns = [...content.columns];
                newColumns[columnIndex] = { ...column, fontWeight: e.target.value };
                updateContent('columns', newColumns);
              }}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
            >
              <option value="normal">Normal</option>
              <option value="medium">Medium</option>
              <option value="semibold">Semibold</option>
              <option value="bold">Bold</option>
            </select>
          </div>
        )}
        {columnSettings.possible_styles.includes('color') && (
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
            <input
              type="color"
              value={column.color || '#1f2937'}
              onChange={(e) => {
                const newColumns = [...content.columns];
                newColumns[columnIndex] = { ...column, color: e.target.value };
                updateContent('columns', newColumns);
              }}
              className="w-full h-8 border border-gray-300 rounded cursor-pointer"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {section ? 'Edit' : 'Add'} {(allSectionTypes && allSectionTypes[sectionType]?.label) || SECTION_TYPES[sectionType]?.label || 'Section'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6">
          {renderEditor()}
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onCancel}
            className={buttonStyles.secondary}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={buttonStyles.primary}
          >
            <Save className="w-4 h-4 mr-2" />
            {section ? 'Update' : 'Add'} Section
          </button>
        </div>
      </div>
    </div>
  );
}
